import { FC, useMemo } from 'react';

import { DisplayOrder, OrderBaseUrls, OrderLineOpenedFrom } from '@ravago/shared/page-data/models/elements';
import { DateUtils } from '@ravago/shared/page-data/utils/date';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { getStatusBadgeVariant, getStatusTranslation } from '../../../utils/order/order-helper.utils';
import { OrderLineItem } from '../order/order-line-item';

import { OrderLineTableMenu } from './order-line-table-menu';

interface MobileOrderLinesTableProps {
   orderLines: DisplayOrder[];
   locale: string;
   startIndex: number;
   endIndex: number;
   acceptWebp?: boolean;
   openedFrom: OrderLineOpenedFrom;
   orderBaseUrls: OrderBaseUrls;
   onOrderLineButtonClick: (orderLine: DisplayOrder) => void;
}

export const MobileOrderLinesTable: FC<MobileOrderLinesTableProps> = ({
   orderLines,
   locale,
   onOrderLineButtonClick,
   startIndex,
   endIndex,
   openedFrom,
   orderBaseUrls,
   acceptWebp
}) => {
   const translate = useTranslations();
   const format = useFormatter();

   const filteredOrderLines = useMemo(
      () => orderLines.filter((_, index) => index >= startIndex && index < endIndex),
      [orderLines, startIndex, endIndex]
   );

   return (
      <Table rowDividers={false} rounded border tableLayout="auto">
         {filteredOrderLines.map((orderLine) => (
            <TableBody key={orderLine.orderNumber}>
               <TableRow>
                  <TableCell>
                     <Box spacing={{ top: '2xs' }} justify="between" items="center">
                        <Badge size="regular" variant={getStatusBadgeVariant(orderLine)}>
                           {translate(getStatusTranslation(orderLine.status))}
                        </Badge>
                        <Box>
                           <OrderLineTableMenu
                              orderLine={orderLine}
                              locale={locale}
                              openedFrom={openedFrom}
                              orderBaseUrls={orderBaseUrls}
                              onOrderLineButtonClick={onOrderLineButtonClick}
                           />
                        </Box>
                     </Box>
                  </TableCell>
               </TableRow>
               <TableRow>
                  <TableCell>
                     <Box onClick={() => onOrderLineButtonClick(orderLine)}>
                        <OrderLineItem order={orderLine} openedFrom={openedFrom} acceptWebp={acceptWebp} />
                     </Box>
                  </TableCell>
               </TableRow>
               <TableRow onClick={() => onOrderLineButtonClick(orderLine)}>
                  <TableCell>
                     <Box direction="column" gap="3xs">
                        <Typography variant="small">
                           {translate('my-orders.table.order-line')} {orderLine.orderNumber}
                        </Typography>
                        <Typography variant="small">
                           {translate('my-orders.table.order-ref')} {orderLine.customerOrderReference || '-'}
                        </Typography>
                     </Box>
                  </TableCell>
               </TableRow>
               <TableRow divider onClick={() => onOrderLineButtonClick(orderLine)}>
                  <TableCell>
                     <Box direction="column" gap="none">
                        {orderLine.isCallOff ? (
                           <Typography variant="small-highlight" component="span">
                              {translate(orderLine.status === 'Closed' ? 'my-orders.delivered' : 'my-orders.call-off-period')}
                           </Typography>
                        ) : (
                           <Typography variant="small-highlight" component="span">
                              {translate(orderLine.status === 'Closed' ? 'my-orders.delivered' : 'my-orders.estimated-delivery')}
                           </Typography>
                        )}
                        <Typography variant="small">
                           {orderLine.deliveryDate ? DateUtils.transformOrderDeliveryDate(format, orderLine.deliveryDate) : '-'}
                        </Typography>
                     </Box>
                  </TableCell>
               </TableRow>
            </TableBody>
         ))}
      </Table>
   );
};
